import { useState, useEffect, useRef } from 'react';
import MessageBubble from './MessageBubble';
import InputField from './InputField';
import TypingIndicator from './TypingIndicator';
import ButtonOptions from './ButtonOptions';
import CompletionScreen from './CompletionScreen';
import api from '../services/api';
import '../styles/ChatInterface.css';

function ChatInterface() {
  const [messages, setMessages] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [isTyping, setIsTyping] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isCompleted, setIsCompleted] = useState(false);
  const [error, setError] = useState(null);
  const [currentButtonOptions, setCurrentButtonOptions] = useState(null);
  const [hasStarted, setHasStarted] = useState(false);
  const [eventName, setEventName] = useState('');
  const [workflowPath, setWorkflowPath] = useState(null);
  const [firstName, setFirstName] = useState('');
  const messagesEndRef = useRef(null);

  // Auto-scroll to bottom
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping]);

  // Initialize session
  useEffect(() => {
    const initSession = async () => {
      try {
        // Clear old event IDs from previous events
        const storedEventId = localStorage.getItem('active-event-id');
        const validEventIds = ['west_side_bazaar_04_30']; // Add new events here as they come

        if (storedEventId && !validEventIds.includes(storedEventId)) {
          console.log('Clearing outdated event ID:', storedEventId);
          localStorage.removeItem('active-event-id');
          localStorage.removeItem('pitch-session-id'); // Clear old session too
        }

        // Get active event ID from localStorage (set by admin panel)
        const activeEventId = localStorage.getItem('active-event-id') || 'west_side_bazaar_04_30';

        if (!activeEventId) {
          setError('No active event. Please contact the event organizer.');
          setIsLoading(false);
          return;
        }

        // Check for existing session in localStorage
        const existingSessionId = localStorage.getItem('pitch-session-id');

        if (existingSessionId) {
          // Try to restore session
          try {
            const sessionData = await api.getSession(existingSessionId);
            console.log('Restored session:', sessionData);
            console.log('Event name from restored session:', sessionData.eventName);
            setSessionId(existingSessionId);
            setMessages(sessionData.conversationHistory || []);
            setIsCompleted(sessionData.completed || false);
            setWorkflowPath(sessionData.workflowPath);
            setEventName(sessionData.eventName || '');
            setHasStarted(sessionData.conversationHistory?.length > 0);
            setIsLoading(false);
            return;
          } catch (err) {
            // Session not found, create new one
            localStorage.removeItem('pitch-session-id');
          }
        }

        // Create new session
        const session = await api.createSession(activeEventId);
        console.log('New session created:', session);
        console.log('Event name from session:', session.eventName);
        setSessionId(session.sessionId);
        setEventName(session.eventName);
        localStorage.setItem('pitch-session-id', session.sessionId);

        // Don't add any messages yet - wait for user to click Start
        setIsLoading(false);
      } catch (err) {
        console.error('Failed to initialize session:', err);
        setError('Failed to start chat. Please refresh the page.');
        setIsLoading(false);
      }
    };

    initSession();
  }, []);

  const handleSendMessage = async (message) => {
    if (!sessionId || isCompleted) return;

    // Clear any button options when user sends a message
    setCurrentButtonOptions(null);

    // Add user message to UI
    setMessages(prev => [...prev, { role: 'user', content: message }]);

    // Show typing indicator
    setIsTyping(true);

    try {
      // Send to API
      const response = await api.sendMessage(sessionId, message);

      console.log('DEBUG FRONTEND: API response =', response);
      console.log('DEBUG FRONTEND: buttonOptions =', response.buttonOptions);

      // Remove typing indicator
      setIsTyping(false);

      // Add AI response
      setMessages(prev => [...prev, { role: 'assistant', content: response.message }]);

      // Update workflow path if provided
      if (response.workflowPath) {
        setWorkflowPath(response.workflowPath);
      }

      // Extract first name from messages if not already set
      if (!firstName) {
        const nameMessage = messages.find(m =>
          m.role === 'assistant' && m.content.toLowerCase().includes('nice to meet you')
        );
        if (nameMessage) {
          const match = nameMessage.content.match(/nice to meet you,?\s+(\w+)/i);
          if (match) {
            setFirstName(match[1]);
          }
        }
      }

      // Show button options if provided
      if (response.buttonOptions) {
        console.log('DEBUG FRONTEND: Setting button options:', response.buttonOptions);
        setCurrentButtonOptions(response.buttonOptions);
      } else {
        console.log('DEBUG FRONTEND: No button options in response');
      }

      // Check if completed
      if (response.completed) {
        setIsCompleted(true);
        localStorage.removeItem('pitch-session-id');
      }
    } catch (err) {
      console.error('Failed to send message:', err);
      setIsTyping(false);
      setMessages(prev => [
        ...prev,
        {
          role: 'assistant',
          content: 'Sorry, something went wrong. Please try again!'
        }
      ]);
    }
  };

  const handleButtonSelect = (selectedOptions) => {
    // Format the response based on selection type
    const message = selectedOptions.length === 1
      ? selectedOptions[0]
      : selectedOptions.join(', ');

    handleSendMessage(message);
  };

  const handleStart = async () => {
    if (hasStarted) return;
    if (!sessionId || isCompleted) return;

    // Mark as started
    setHasStarted(true);

    // Show typing indicator
    setIsTyping(true);

    try {
      // Send "start" message to backend WITHOUT showing it in the UI
      const response = await api.sendMessage(sessionId, 'start');

      console.log('DEBUG FRONTEND: API response =', response);
      console.log('DEBUG FRONTEND: buttonOptions =', response.buttonOptions);

      // Remove typing indicator
      setIsTyping(false);

      // Add AI response
      setMessages([{ role: 'assistant', content: response.message }]);

      // Show button options if provided
      if (response.buttonOptions) {
        console.log('DEBUG FRONTEND: Setting button options:', response.buttonOptions);
        setCurrentButtonOptions(response.buttonOptions);
      } else {
        console.log('DEBUG FRONTEND: No button options in response');
      }

      // Check if completed
      if (response.completed) {
        setIsCompleted(true);
        localStorage.removeItem('pitch-session-id');
      }
    } catch (err) {
      console.error('Failed to start conversation:', err);
      setIsTyping(false);
      setMessages([
        {
          role: 'assistant',
          content: 'Sorry, something went wrong. Please try again!'
        }
      ]);
    }
  };

  const handleRestart = async () => {
    if (!sessionId) return;

    try {
      setIsLoading(true);
      const newSession = await api.resetSession(sessionId);

      // Update to new session
      setSessionId(newSession.sessionId);
      setEventName(newSession.eventName);
      localStorage.setItem('pitch-session-id', newSession.sessionId);

      // Clear state
      setMessages([]);
      setCurrentButtonOptions(null);
      setIsCompleted(false);
      setHasStarted(false);
      setError(null);
      setIsLoading(false);
    } catch (err) {
      console.error('Failed to restart:', err);
      setError('Failed to restart. Please refresh the page.');
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="chat-interface loading">
        <div className="loader">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="chat-interface error">
        <div className="error-message">{error}</div>
      </div>
    );
  }

  return (
    <div className="chat-interface">
      <div className="chat-header">
        <img
          src="/assets/paf-buffalo-logo.png"
          alt="Pitch A Friend Logo"
          className="chat-logo"
        />
        <h1>Pitch-A-Friend</h1>
        <p>Let's find you a great connection!</p>
        {hasStarted && (
          <button
            className="restart-button"
            onClick={handleRestart}
            disabled={isLoading}
          >
            Start Over
          </button>
        )}
      </div>

      <div className="messages-container">
        {!hasStarted ? (
          <div className="welcome-screen">
            <div className="welcome-message">
              <h2>Welcome to {eventName || 'Pitch-A-Friend'}!</h2>
              <p>I'm here to help make great connections tonight.</p>
            </div>
            <button
              className="start-button"
              onClick={handleStart}
              disabled={isLoading}
            >
              Get Started
            </button>
          </div>
        ) : (
          <>
            {messages.map((msg, idx) => (
              <MessageBubble
                key={idx}
                role={msg.role}
                content={msg.content}
              />
            ))}

            {isTyping && (
              <div className="message-bubble assistant typing">
                <TypingIndicator />
              </div>
            )}

            {currentButtonOptions && !isTyping && (
              <ButtonOptions
                options={currentButtonOptions.options}
                type={currentButtonOptions.type}
                minSelections={currentButtonOptions.minSelections || 1}
                onSelect={handleButtonSelect}
              />
            )}
          </>
        )}

        <div ref={messagesEndRef} />
      </div>

      {!isCompleted ? (
        <div className="input-container">
          <InputField
            onSend={handleSendMessage}
            disabled={!hasStarted || isTyping || isCompleted || currentButtonOptions !== null}
          />
        </div>
      ) : (
        <CompletionScreen
          sessionId={sessionId}
          workflowPath={workflowPath}
          firstName={firstName}
        />
      )}
    </div>
  );
}

export default ChatInterface;
